/**
 * useMerchantCouponActions Hook - Coordinator Pattern Implementation
 *
 * This hook coordinates merchant coupon-related actions:
 * - Create new coupons
 *
 * Pattern: COORDINATOR
 * - Orchestrates coupon mutations
 * - Returns Promises (no data storage)
 * - Loading states per action
 * - No computed values or aggregation
 *
 * IMPORTANT: This hook handles coupon modifications. For reading
 * coupon data, use the useMerchantCoupons aggregator hook.
 *
 * NOTE: Update and distribution endpoints are not yet implemented in the API.
 * When they become available, add:
 * - useUpdateCoupon
 * - useCreateCouponDistribution
 */

import { useCreateCoupon } from '@/generated/hooks';
import type { CreateCouponBodyType, CreateCouponResponseType } from '@zlinkx/shared-v2';

// ============================================================================
// TYPES
// ============================================================================

/**
 * Options for useMerchantCouponActions hook
 */
interface UseMerchantCouponActionsOptions {
	/**
	 * Callback on successful coupon creation
	 */
	onCreateSuccess?: (data: CreateCouponResponseType['data']) => void;
	/**
	 * Callback on coupon creation error
	 */
	onCreateError?: (error: Error) => void;
}

/**
 * Loading states for actions
 */
interface MerchantCouponActionsLoading {
	createCoupon: boolean;
}

/**
 * Return type for useMerchantCouponActions hook
 */
interface UseMerchantCouponActionsReturn {
	/**
	 * Create a new coupon
	 */
	createCoupon: (
		merchantId: number,
		data: CreateCouponBodyType,
	) => Promise<CreateCouponResponseType['data']>;
	/**
	 * Loading states per action
	 */
	isLoading: MerchantCouponActionsLoading;
}

// ============================================================================
// HOOK IMPLEMENTATION
// ============================================================================

/**
 * Coordinates merchant coupon actions
 *
 * @example
 * ```tsx
 * function CouponManagementPage() {
 *   const actions = useMerchantCouponActions({
 *     onCreateSuccess: (result) => {
 *       toast.success('Coupon created');
 *       navigate(`/merchant/${merchantId}/coupons/${result.coupon_id}`);
 *     },
 *     onCreateError: (error) => {
 *       toast.error(`Failed to create coupon: ${error.message}`);
 *     },
 *   });
 *
 *   const handleCreate = async (formData: CreateCouponBodyType) => {
 *     try {
 *       await actions.createCoupon(merchantId, formData);
 *     } catch (error) {
 *       // Error handled by onCreateError
 *     }
 *   };
 *
 *   return (
 *     <form onSubmit={handleCreate}>
 *       <button
 *         type="submit"
 *         disabled={actions.isLoading.createCoupon}
 *       >
 *         Create Coupon
 *       </button>
 *     </form>
 *   );
 * }
 * ```
 */
export function useMerchantCouponActions(
	options: UseMerchantCouponActionsOptions = {},
): UseMerchantCouponActionsReturn {
	const { onCreateSuccess, onCreateError } = options;

	// ========================================
	// Generated Hooks (API layer)
	// ========================================

	const createMutation = useCreateCoupon({
		onSuccess: data => {
			onCreateSuccess?.(data);
		},
		onError: error => {
			onCreateError?.(error);
		},
	});

	// ========================================
	// Action Functions
	// ========================================

	/**
	 * Create a new coupon
	 */
	const createCoupon = async (
		merchantId: number,
		data: CreateCouponBodyType,
	): Promise<CreateCouponResponseType['data']> => {
		const response = await createMutation.mutateAsync({
			merchantId,
			data,
		});
		return response;
	};

	// ========================================
	// Return Coordinator Pattern
	// ========================================
	return {
		// Actions
		createCoupon,

		// Loading states
		isLoading: {
			createCoupon: createMutation.isPending,
		},
	};
}

// ============================================================================
// EXPORTS
// ============================================================================

export default useMerchantCouponActions;

export type {
	UseMerchantCouponActionsOptions,
	MerchantCouponActionsLoading,
	UseMerchantCouponActionsReturn,
};
